import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { jwtDecode } from 'jwt-decode';
import { getCookie, resetTokensAndSendToLogin, setCookie } from 'helpers/cookies';
import blobErrorToObject from 'helpers/blobErrorToObject';
import users from './users';
import { requestTemplate } from 'api';
import { HistoryResponseV2Type } from './chats';
import { functionCollectBrowserData } from 'helpers/browserData/functionCollectBrowserData';
import { refresTokensHandle } from 'helpers/tokens';

const correctHeaders = window.location?.href?.includes('localhost:3000')
  ? { "x-forwarded-for": "0.0.0.0", "Access-Control-Allow-Origin": "*"}
  : { "Access-Control-Allow-Origin": "*" };

const aiAssistantTemplate = axios.create({
  baseURL: window.REACT_APP_AI_GENERATION_URL !== 'REPLACE_REACT_APP_AI_GENERATION_URL' ? window.REACT_APP_AI_GENERATION_URL : process.env.REACT_APP_AI_GENERATION_URL || 'http://localhost:8099/api',
  // withCredentials: true,
  headers: correctHeaders,
});

aiAssistantTemplate.interceptors.request.use(async(config: InternalAxiosRequestConfig) => {
  let accessToken = getCookie('athena-token');
  let decodeInfo: any;
  if (accessToken) {
    try {
      decodeInfo = jwtDecode(accessToken);
    } catch (error) {
      Promise.reject('Error decoding token');
    }
    const expTime = decodeInfo?.exp && decodeInfo.exp * 1000;
    const curTime = new Date().getTime();

    if (expTime && expTime - curTime <= 50000) {
      const refresh_token = getCookie('refresh_token');      
      if (refresh_token) {
        try {
          const newTokens = await refresTokensHandle(refresh_token);
          accessToken = newTokens.access_token;
        } catch (error) {
          Promise.reject('Error refreshing tokens');
          resetTokensAndSendToLogin();
        };
      };
    };

    config.headers!.Authorization = `Bearer ${accessToken}`
  } else {
    setCookie('athena-token', '', 1);
  }
  return config;
});

let cntTryingUpdateFingerprint = 0;

aiAssistantTemplate.interceptors.response.use(( response: AxiosResponse ) => {
  return response
}, async (error: AxiosError<any>) => {
  if (error.response?.status === 401 && cntTryingUpdateFingerprint < 1) {
    cntTryingUpdateFingerprint++;

    try {
      const formData = new FormData();
      const browserData = await functionCollectBrowserData();
      formData.append('browser_data', JSON.stringify(browserData) || '');

      const freshTokens = await users.updateFingerprint(formData);
      setCookie('athena-token', freshTokens.data.access_token, 1);
      setCookie('refresh_token', freshTokens.data.refresh_token, 1);

      return aiAssistantTemplate.request(error.request.responseURL);
    } catch {
      resetTokensAndSendToLogin();
    }
  } else if (cntTryingUpdateFingerprint >= 1) {
    resetTokensAndSendToLogin();
  };

  if (error?.response?.data.constructor.name === 'Blob'){
    error.response.data = await blobErrorToObject(error?.response?.data)
  };
  return Promise.reject(error)
});

const generateDocumentByRequirements = (payload: { room_id: string, folder_id: string | null, query: string, document_name: string }) =>
  aiAssistantTemplate.post(`/document_generator/generate/`, payload);

const checkTranscription = (payload: { document_id: string }) =>
  aiAssistantTemplate.get<any>(`/speech-to-text/transcriptions/${payload.document_id}/`);

const getTranscriptionFile = (document_id: string) =>
  aiAssistantTemplate.get<any>(`/speech-to-text/transcriptions/${document_id}/file/`);

const sendOnTranscription = (payload: { document_id: string, speakers?: number, language?: string }) =>
  aiAssistantTemplate.post(`/speech-to-text/generate/`, payload);


const checkAnalysis = (payload: { document_id: string }) =>
  aiAssistantTemplate.get<any>(`/report/${payload.document_id}/`);

const sendToAnalysis = (payload: { document_id: string, user_input?: string }) =>
  aiAssistantTemplate.post<any>('/report/generate/', payload);

const getAnalysisReport = (payload: { document_id: string }) =>
  aiAssistantTemplate.get<any>(`/report/${payload.document_id}/file/`);

const getChatAIAssistant = (payload: { access_token: string }) =>
  requestTemplate.get<any>(`/document-assistant/?access_token=${payload.access_token}`);

const createChatAIAssistant = (payload: { access_token: string }) =>
  requestTemplate.post<any>(`/document-assistant/`, payload);

const getMessagesChatAIAssistant = (payload: { access_token: string, page: number, per_page: number, filters?: any, sort?: any }) =>
  requestTemplate.get<HistoryResponseV2Type>(`/document-assistant/message/?access_token=${payload.access_token}&page=${payload.page}&per_page=${payload.per_page}`);

const sendMessageChatAIAssistant = (payload: { access_token: string, text: string }) =>
  aiAssistantTemplate.post<any>(`/document-assistant/message/`, payload);


const aiAssistant = {
  generateDocumentByRequirements,
  checkTranscription,
  getTranscriptionFile,
  sendOnTranscription,

  checkAnalysis,
  sendToAnalysis,
  getAnalysisReport,

  getChatAIAssistant,
  createChatAIAssistant,
  getMessagesChatAIAssistant,
  sendMessageChatAIAssistant,
};

export default aiAssistant;
